import React, { useState } from "react";

import { CardContent } from "./ui/card";
import { truncateDecimals } from "../utils/numberFormatter";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import { Skeleton } from "./ui/skeleton";
import { Button } from "./ui/button";
import coin from "../assets/images/placeholder/crypto-colour.png";
import { X, ChevronDown, Loader2, Check } from "lucide-react";
import BankSelect from "./BankSelect";
import info from "../assets/images/trading/info.svg";
export default function WithdrawPage({
  isLoadingFiat,
  selectedFiat,
  fiatBalance,
  fiat,
  selectedBank,
  onAmountChange,
  amount,
  isWithdraw,
  bankData,
  flagSelectedBank,
  onSelect,
  onAddBank,
  flagExceed,
  instantPayment = true,
  setInstantPayment,
  instantFee,
  handleContinue,
  disabled,
  open,
  setOpen,
  handleSelect,
  setAmount,
  loadingExisting,
  minWithdrawal,
}) {
  const [errors, setErrors] = useState({
    selectedFiat: "",
    amount: "",
    selectedBank: "",
  });

  const parseBalance = (balanceStr) => {
    if (!balanceStr) return 0;
    const cleaned = balanceStr
      .replace(/^(ZAR|R)\s*/i, "")
      .replace(/\s/g, "")
      .replace(/,/g, "");
    return parseFloat(cleaned) || 0;
  };

  const parseNumericValue = (value) => {
    if (!value) return 0;
    if (typeof value === "number") return value;
    const cleaned = String(value).replace(/\s/g, "").replace(/,/g, "");
    return parseFloat(cleaned) || 0;
  };

  const formatBalanceForDisplay = (balanceStr) => {
    if (!balanceStr) return "0.00";
    return balanceStr.replace(/^ZAR\s*/i, "").replace(/,/g, " ");
  };

  const clearError = (fieldName) => {
    setErrors((prev) => ({
      ...prev,
      [fieldName]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {
      selectedFiat: "",
      amount: "",
      selectedBank: "",
    };

    let isValid = true;

    if (!selectedFiat) {
      newErrors.selectedFiat = "Please select a currency.";
      isValid = false;
    }

    const rawAmount =
      typeof amount === "string"
        ? amount.replace(/,/g, "").replace(/\s/g, "")?.trim()
        : amount;
    const parsedAmount = Number(rawAmount);

    if (
      rawAmount === "" ||
      rawAmount === null ||
      isNaN(parsedAmount) ||
      parsedAmount <= 0
    ) {
      newErrors.amount = "Please enter a valid amount greater than zero.";
      isValid = false;
    } else {
      if (instantPayment && parsedAmount < 60) {
        newErrors.amount = "Amount must be at least R60 for express withdrawal";
        isValid = false;
      } else {
        const minWithdrawalNum = parseNumericValue(minWithdrawal);
        if (minWithdrawalNum > 0 && parsedAmount < minWithdrawalNum) {
          newErrors.amount = `Minimum withdrawal cannot be less than R${truncateDecimals(minWithdrawalNum, 2)}`;
          isValid = false;
        } else {
          const balanceNum = parseBalance(fiatBalance);
          const feeNum = instantPayment ? parseFloat(instantFee) || 0 : 0;
          const availableBalance = balanceNum - feeNum;

          if (parsedAmount > availableBalance) {
            newErrors.amount = "This amount exceeds your available balance";
            isValid = false;
          }
        }
      }
    }

    if (!selectedBank) {
      newErrors.selectedBank = "Please select a bank account.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const maxAmount = () => {
    const balanceNum = parseBalance(fiatBalance);
    if (instantPayment) {
      const finalMax = balanceNum - parseFloat(instantFee || 0);
      setAmount(finalMax);
    }
    if (!instantPayment) {
      setAmount(balanceNum);
    }
    clearError("amount");
  };

  const handleInstantPaymentChange = (checked) => {
    setInstantPayment(checked);
    clearError("amount");
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    onAmountChange(e);
    if (value && String(value).trim() !== "") {
      const rawAmount = value.replace(/,/g, "").replace(/\s/g, "")?.trim();
      const parsedAmount = Number(rawAmount);

      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        clearError("amount");
      } else {
        if (instantPayment && parsedAmount < 60) {
          setErrors((prev) => ({
            ...prev,
            amount: "Amount must be at least R60 for express withdrawal",
          }));
        } else {
          const minWithdrawalNum = parseNumericValue(minWithdrawal);
          const balanceNum = parseBalance(fiatBalance);
          const feeNum = instantPayment ? parseFloat(instantFee) || 0 : 0;
          const availableBalance = balanceNum - feeNum;

          if (minWithdrawalNum > 0 && parsedAmount < minWithdrawalNum) {
            setErrors((prev) => ({
              ...prev,
              amount: `Minimum withdrawal cannot be less than R${truncateDecimals(minWithdrawalNum, 2)}`,
            }));
          } else if (parsedAmount > availableBalance) {
            console.log("parsedAmount", parsedAmount);
            console.log("availableBalance", availableBalance);
            setErrors((prev) => ({
              ...prev,
              amount: "This amount exceeds your available balance",
            }));
          } else {
            clearError("amount");
          }
        }
      }
    } else {
      clearError("amount");
    }
  };

  const handleFiatSelect = (asset) => {
    handleSelect(asset);
    clearError("selectedFiat");
  };

  const handleBankSelect = (bank) => {
    onSelect(bank);
    clearError("selectedBank");
  };

  const handleContinueClick = () => {
    if (validateForm()) {
      handleContinue();
    }
  };

  return (
    <CardContent className="px-2 sm:px-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
        <div className="space-y-2 col-span-1">
          <label className="block text-[14px] text-[#6D6584] mb-2">
            Withdraw
          </label>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              {isLoadingFiat ? (
                <div className="w-full flex items-center justify-between h-[70px] md:h-[88px] px-4 rounded-lg border border-[#CECBD7] bg-muted/50">
                  <div className="flex items-center gap-3 ">
                    <Skeleton className="w-10 h-10 rounded-full bg-gray-300" />

                    <div className="flex flex-col gap-1">
                      <Skeleton className="w-10 h-5 rounded-full bg-gray-300" />
                    </div>
                  </div>
                  <Skeleton className="w-6 h-6 rounded-full bg-gray-300" />
                </div>
              ) : (
                <Button
                  variant="outline"
                  className={`w-full flex items-center justify-between h-[70px] md:h-[88px] rounded-lg hover:border-brand-primary transition-colors ${
                    errors.selectedFiat
                      ? "border-rose-500 border-2"
                      : "border-[#CECBD7]"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center">
                      <img
                        src={selectedFiat?.image || coin}
                        alt={selectedFiat?.assetName || "Default Asset"}
                        width={32}
                        height={32}
                        className="text-white"
                      />
                    </div>
                    <div className="text-left">
                      <div
                        className={`font-semibold text-[16px] ${
                          errors.selectedFiat
                            ? "text-rose-500"
                            : "text-[#423E51]"
                        }`}
                      >
                        {selectedFiat?.asset?.toUpperCase() || "Select..."}
                      </div>
                    </div>
                  </div>
                  <ChevronDown
                    className={
                      errors.selectedFiat ? "text-rose-500" : "text-gray-400"
                    }
                  />
                </Button>
              )}
            </DialogTrigger>

            <DialogContent className="max-w-sm rounded-2xl p-6">
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  onClick={() => setOpen(false)}
                  className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                >
                  <X size={18} className="text-gray-700" />
                </Button>
              </DialogClose>

              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-gray-800">
                  Select an Asset
                </DialogTitle>
              </DialogHeader>

              <div className="mt-4 flex flex-col space-y-3 max-h-72 overflow-y-auto">
                {fiat &&
                  fiat.map((asset) => {
                    return (
                      <button
                        key={asset.id}
                        onClick={() => {
                          handleFiatSelect(asset);
                        }}
                        className="flex items-center gap-3 px-3 py-3 border rounded-lg border-transparent bg-gray-50 hover:border-rose-200"
                      >
                        <img
                          src={asset.image}
                          alt={asset.assetName}
                          className="w-6 h-6"
                        />
                        <div className="flex flex-col text-left">
                          <span>{asset.assetName}</span>
                          <span className="text-xs text-gray-500">
                            {asset.asset?.toUpperCase()}
                          </span>
                        </div>
                      </button>
                    );
                  })}
              </div>
            </DialogContent>
          </Dialog>
          {errors.selectedFiat && (
            <p className="mt-2 text-sm text-rose-500">{errors.selectedFiat}</p>
          )}
        </div>
        <div className="space-y-2 col-span-1 md:col-span-3">
          <label className="text-[14px] text-[#6D6584]">
            Available balance
          </label>
          <div className="h-[70px] md:h-[88px] px-6 py-2 rounded-lg bg-gray-50 flex-1 flex items-center">
            <div className="text-xl font-semibold text-[#6D6584]">
              {formatBalanceForDisplay(fiatBalance)}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-[14px] text-[#6D6584] mb-2">
          Enter amount
        </label>
        <div
          className={`relative flex items-center border rounded-lg px-6 py-2 h-[70px] md:h-[88px] bg-transparent transition-colors ${
            flagExceed || errors.amount
              ? "border-rose-500 border-2"
              : "border-[#CECBD7]"
          }`}
        >
          <span
            className={`lg:text-[24px] text-[20px] font-semibold mr-2 ${
              flagExceed || errors.amount ? "text-rose-500" : "text-[#6D6584]"
            }`}
          >
            {selectedFiat?.asset === "zar" || !selectedFiat?.asset
              ? "R"
              : selectedFiat?.asset?.toUpperCase()}
          </span>
          <input
            type="text"
            placeholder="0"
            value={amount ? `${amount}` : ""}
            onChange={handleAmountChange}
            className={`flex-1 outline-none bg-transparent lg:text-[24px] text-[20px] font-semibold outline-none 
              focus-visible:ring-0 focus-visible:outline-none transition-colors duration-200 ease-in-out placeholder:text-[#6D6584]
              ${flagExceed || errors.amount ? "text-rose-500" : "text-[#6D6584]"}`}
          />
          <Button
            onClick={maxAmount}
            variant="outline"
            className="rounded-full w-[76px] text-[#6D6584] border-[#EBE9EF]"
          >
            Max
          </Button>
        </div>
        {(errors.amount || flagExceed) && (
          <p className="mt-2 text-sm text-rose-500">
            {errors.amount || "This amount exceeds your available balance"}
          </p>
        )}
      </div>

      <div className="md:mb-6 mb-0">
        <label className="block text-[14px] text-[#6D6584] mb-2">
          Withdraw to
        </label>
        <BankSelect
          flagSelectedBank={flagSelectedBank || errors.selectedBank}
          isWithdraw={isWithdraw}
          bankData={bankData}
          onSelect={handleBankSelect}
          onAddBank={onAddBank}
          parentSelectedBank={selectedBank}
          loadingExisting={loadingExisting}
          className="h-[70px] md:h-[88px]"
        />
        {errors.selectedBank && (
          <p className="mt-2 text-sm text-rose-500">{errors.selectedBank}</p>
        )}
      </div>
      <div className="mb-6 flex items-center gap-3 md:border-none border border-[#F1EDF7] md:p-0 p-4 rounded-b-2xl">
        <button
          type="button"
          onClick={() => handleInstantPaymentChange(!instantPayment)}
          className={`w-[32px] h-[32px] rounded flex items-center justify-center transition-colors flex-shrink-0 mt-0.5 ${
            instantPayment ? "bg-[#EB3366]" : "bg-gray-200"
          }`}
        >
          {instantPayment && (
            <Check className="w-[24px] h-[24px] md:w-[27px] md:h-[27px] text-white" />
          )}
        </button>
        <div
          className="flex-1 cursor-pointer"
          onClick={() => handleInstantPaymentChange(!instantPayment)}
        >
          <div className="flex items-center gap-2">
            <span className="text-[16px] font-semibold text-[#423E51] cursor-pointer">
              Express Withdrawal
            </span>
            <img
              src={info}
              alt="info"
              className="w-[16px] h-[16px] md:block hidden"
              style={{
                filter:
                  "brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1352%) hue-rotate(315deg) brightness(95%) contrast(92%)",
              }}
            />
          </div>
          <p className="text-[14px] text-[#6D6584]">
            Express withdrawal fee of R60. This can take up to 30mins.
          </p>
        </div>
        <img
          src={info}
          alt="info"
          className="w-[24px] h-[24px] md:hidden block cursor-pointer"
          style={{
            filter:
              "brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(1352%) hue-rotate(315deg) brightness(95%) contrast(92%)",
          }}
        />
      </div>

      <div className="flex justify-center mt-12">
        <Button
          disabled={
            disabled ||
            !!errors.amount ||
            !!errors.selectedFiat ||
            !!errors.selectedBank
          }
          onClick={handleContinueClick}
          className={`px-6 py-4 rounded-full font-semibold transition-opacity w-full sm:min-w-[302px] max-w-sm h-[48px] text-sm sm:text-base ${
            disabled ||
            !!errors.amount ||
            !!errors.selectedFiat ||
            !!errors.selectedBank
              ? "bg-[#F2F1F4] text-[#9D96AF] cursor-not-allowed"
              : "bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white hover:opacity-90"
          }`}
        >
          {disabled ? <Loader2 className="animate-spin w-4 h-4" /> : "Continue"}
        </Button>
      </div>
    </CardContent>
  );
}
